import React, {useState} from "react";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {deleteEmail, selectIsLoading, selectIsLoadingMore, selectIsUpdating} from "../../redux/email/emailListSlice";
import {ColumnProps} from "rc-table/lib/sugar/Column";
import {Caption, Heading4, Paragraph, TextStyles} from "../text";
import Moment from "moment";
import Table from "rc-table";
import {Button} from "../buttons/Button";
import {useModalRouting} from "../../utils/useModalRouting";
import {EmailModel} from "../../models/email";
import {TableHeader} from "../table/TableHeader";
import {TableCell} from "../table/TableCell";
import {ArrowDownIcon} from "../icons";
import {EmailDetailSendModal} from "./EmailDetailSendModal";
import {AlertDialog} from "../dialogs/AlertDialog";
import {useRouter} from "next/router";

export interface EmailsTableProps {
  emails: EmailModel[]
  onLoadMoreClicked: () => void
}

const EmailTableActionButton: React.FC<{ email: EmailModel }> = ({ email }) => {
  const isUpdating = useAppSelector(state => selectIsUpdating(state, email.id))
  const dispatch = useAppDispatch()
  const { createRoute } = useModalRouting()
  const router = useRouter()

  const [sendModalOpen, setSendModalOpen] = useState({ isOpen: false, isTest: true })
  const [emailIdToDelete, setEmailIdToDelete] = useState<string>()

  return <div className="relative">
    <div className="absolute top-0 left-0">
      <Button
        isLoading={isUpdating}
        buttonStyle="secondary"
        trailingIconSizeClass="h-3 w-3"
        trailingicon={<ArrowDownIcon />}>
        <span className="capitalize">Actions</span>
      </Button>
    </div>

    <select
      id="actions"
      name="actions"
      value={'blank'}
      onClick={e => {
        e.stopPropagation()
      }}
      onChange={e => {
        if (!e.target.value) return

        const emailId = email.id
        if (!emailId) return

        switch (e.target.value) {
          case 'edit':
            router.push(
              createRoute({ name: 'email_detail', query: { email_id: email.id }, returnTo: '/admin/emails' }),
              undefined,
              { shallow: true }
            )
            break;
          case 'delete':
            setEmailIdToDelete(email.id)
            break;
          case 'send_email':
            setSendModalOpen({ isOpen: true, isTest: false })
            break;
          case 'send_test':
            setSendModalOpen({ isOpen: true, isTest: true })
            break;
        }
      }}
      className={`appearance-none opacity-0 transition-colors bg-fg-primary hover:bg-fg-gray-surface-dark border-0 cursor-pointer text-center focus:ring-0 rounded-full px-5 py-2 flex flex-row items-center ${TextStyles.Body}`}
    >
      <option key={'blank'} value={'blank'}/>
      <option key={'edit'} value={'edit'}>Edit</option>
      <option key={'delete'} value={'delete'}>Delete</option>
      <option key={'send_email'} value={'send_email'}>Send Email</option>
      <option key={'send_test'} value={'send_test'}>Send Test</option>
    </select>

    <AlertDialog
      isOpen={!!emailIdToDelete}
      message={'Are you sure you want to delete this email?'}
      title={'Delete Email?'}
      buttons={[
        {
          title: { key: 'common:common_title_delete' },
          onClick: () => {
            emailIdToDelete && dispatch(deleteEmail(emailIdToDelete))
            setEmailIdToDelete(undefined)
          },
          style: 'destructive'
        },
        {
          title: { key: 'common:common_title_cancel' },
          onClick: () => setEmailIdToDelete(undefined),
          style: 'cancel'
        }
      ]}
    />

    <EmailDetailSendModal
      isOpen={sendModalOpen.isOpen}
      email={email}
      isTest={sendModalOpen.isTest}
      onClose={() => {
        setSendModalOpen({ isOpen: false, isTest: true })
      }}
    />
  </div>
}

export const EmailsTable: React.FC<EmailsTableProps> = ({ emails, onLoadMoreClicked }) => {
  const isLoading = useAppSelector(selectIsLoading)
  const isLoadingMore = useAppSelector(selectIsLoadingMore)

  const columns: ColumnProps<EmailModel>[] = [
    {
      title: <TableHeader isFirst={true}>
        <Heading4 className="text-fg-gray-dark">Title</Heading4>
      </TableHeader>,
      dataIndex: 'title',
      render: (text, record) => (
        <TableCell>
          <div>
            <Paragraph>{`${record.name}`}</Paragraph>
            <Caption className="text-fg-gray-dark">Subject: {`${record.formattedSubject}`}</Caption>
          </div>
        </TableCell>
      )
    },
    {
      title: <TableHeader>
        <Heading4 className="text-fg-gray-dark">Create On</Heading4>
      </TableHeader>,
      dataIndex: 'created_at',
      render: (text, record) => (
        <TableCell>
          <Paragraph>{Moment(record.createdAt).format('MMM D, YYYY')}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader>
        <Heading4 className="text-fg-gray-dark">Deliveries</Heading4>
      </TableHeader>,
      dataIndex: 'deliveries',
      render: (text, record) => (
        <TableCell>
          <Paragraph>{record.stats?.delivered ?? 0}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader>
        <Heading4 className="text-fg-gray-dark">Open %</Heading4>
      </TableHeader>,
      dataIndex: 'open_rate',
      render: (text, record) => {
        let deliveries = record.stats?.delivered ?? 0
        deliveries = deliveries ? deliveries : 1
        const opens = record.stats?.opens ?? 0

        const openRate = Math.min(opens / deliveries, 1)
        const openRatePercentage = Math.round(openRate * 100)

        return <TableCell>
          <Paragraph>{openRatePercentage}%</Paragraph>
        </TableCell>
      }
    },
    {
      title: <TableHeader>
        <Heading4 className="text-fg-gray-dark">Click Thru %</Heading4>
      </TableHeader>,
      dataIndex: 'click_rate',
      render: (text, record) => {
        let deliveries = record.stats?.delivered ?? 0
        deliveries = deliveries ? deliveries : 1
        const clicks = record.stats?.clicks ?? 0

        const clickRate = Math.min(clicks / deliveries, 1)
        const clickRatePercentage = Math.round(clickRate * 100)

        return <TableCell>
          <Paragraph>{clickRatePercentage}%</Paragraph>
        </TableCell>
      }
    },
    {
      title: <TableHeader isLast={true}>

      </TableHeader>,
      dataIndex: 'action',
      render: (text, record) => (
        <TableCell>
          <EmailTableActionButton email={record} />
        </TableCell>
      )
    }
  ]

  return <Table
    className="rounded-lg border border-fg-gray-surface fg-table"
    scroll={{x: true}}
    data={emails}
    columns={columns}
    emptyText={() => {
      return <div className="w-full h-16 flex items-center justify-center">
        <Paragraph>No Emails</Paragraph>
      </div>
    }}
    footer={() => {
      return <div className="w-full flex items-center justify-center py-8 border-t border-fg-gray-surface">
        <Button
          buttonStyle="secondary"
          isLoading={isLoadingMore}
          onClick={() => {
            onLoadMoreClicked()
          }}
        >
          Load More
        </Button>
      </div>
    }}
  />
}